// noinspection NpmUsedModulesInstalled,ES6CheckImport,DuplicatedCode

import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { eachDayOfInterval } from 'date-fns';
import ReportSummaryContext from '@/modules/reports/contexts/ReportSummaryContext';
import Paper from '@/modules/common/components/Paper';
import Content from '@/modules/common/components/Content';
import Spinner from '@/modules/common/components/Spinner';
import ErrorFetch from '@/modules/common/components/ErrorFetch';
import ContentBody from '@/modules/common/components/ContentBody';
import ContentHeader from '@/modules/common/components/ContentHeader';
import { withFetch } from '~/hocs/fetch';
import { withRouter } from '~/hocs/router';
import { actionDockSetExpandReports } from '@/modules/application/actions/dock';
import { parseDate, formatDate, isEmptyObject } from '~/common';
import Top from './Top';
import TableLeft from './TableLeft/TableLeft';
import TableRight from './TableRight/TableRight';
import '../../styles/ReportSummary.scss';

const initialValuesFilter = {
    typeName: [],
    statusInternal: '',
    date: {from: new Date(), to: new Date()},
};

function isFiltered(urlSearchParams) {
    return (
        urlSearchParams.get('type_name') ||
        urlSearchParams.get('status_internal') ||
        urlSearchParams.get('date_from') ||
        urlSearchParams.get('date_to')
    );
}

let abortController;

const ReportSummary = (props) => {
    let breadcrumbs = [
        {name: 'Отчеты'},
        {name: 'Сводный'},
    ];

    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);
    const [dateRange, setDateRange] = useState({from: new Date(), to: new Date()});
    const [valuesFilter, setValuesFilter] = useState(initialValuesFilter);
    const [ordering, setOrdering] = useState('primary');

    const { offerName, affiliateName } = useParams();

    if (offerName) {
        breadcrumbs[1].to = '/reports/summary/';
        breadcrumbs.push({name: offerName});
    }
    if (affiliateName) {
        breadcrumbs[2].to = `/reports/summary/${offerName}/`;
        breadcrumbs.push({name: affiliateName});
    }

    const fetch = () => {
        abortController = new AbortController();

        let url = '/api/reports/summary/';
        if (affiliateName) {
            url += `${offerName}/${affiliateName}/`;
        }
        else if (offerName) {
            url += `${offerName}/`;
        }
        url += `?${props.router.urlSearchParams.toString()}`;
        setLoading(true);
        props.fetch.get(url, abortController)
            .then(({ok, data}) => {
                if (ok) {
                    let _report = {};
                    if (data.report) {
                        let entries = Object.entries(data.report);
                        entries = entries.map((element) => [' ' + element[0], element[1]]);
                        // если ключ число, то JS сортирует объект автоматически
                        entries = entries.sort(([a, ], [b,]) => a > b ? 1 : -1);
                        _report = Object.fromEntries(entries);
                    }
                    setReport(_report);
                }
                setLoading(false);
            })
            .catch((error) => {
                setReport(null);
                if (error.name !== 'AbortError') {
                    setLoading(false);
                }
            })
            .finally(() => {
                abortController = null;
            });
    };

    useEffect(() => {
        props.dockSetExpandReports(true);
        return () => {
            if (abortController) {
                abortController.abort();
            }
        };
    }, []);

    useEffect(() => {
        const date = new Date();
        let date_from = props.router.urlSearchParams.get('date_from');
        let date_to = props.router.urlSearchParams.get('date_to');
        date_from = parseDate(date_from, 'dd.MM.yy');
        date_to = parseDate(date_to, 'dd.MM.yy');
        if (!date_from) {
            date_from = new Date(date.getFullYear(), date.getMonth(), 1);
        }
        if (!date_to) {
            date_to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        }
        setDateRange({from: date_from, to: date_to});
        setValuesFilter({
            typeName: props.router.getUrlSearchParam('type_name').split('||'),
            statusInternal: props.router.getUrlSearchParam('status_internal'),
            date: {from: date_from, to: date_to}
        });
        fetch();
    }, [props.history.location.search]);

    const handleClickResetFilter = () => {
        props.history.push({search: ''});
    };

    const handleSubmitFilter = (event) => {
        event.preventDefault();
        props.router.urlSearchParams.set('type_name', valuesFilter.typeName.join('||'));
        props.router.urlSearchParams.set('status_internal', valuesFilter.statusInternal);
        props.router.urlSearchParams.set('date_from', formatDate(valuesFilter.date.from));
        props.router.urlSearchParams.set('date_to', formatDate(valuesFilter.date.to));
        props.history.push({search: props.router.urlSearchParams.toString()});
    };

    const handleClickOrder = (newOrdering) => {
        // noinspection JSCheckFunctionSignatures
        let entries = Object.entries(report);
        if (newOrdering === 'primary') {
            entries = entries.sort(([a, ], [b,]) => a > b ? 1 : -1);
        }
        else if (newOrdering === '-primary') {
            entries = entries.sort(([a,], [b,]) => a < b ? 1 : -1);
        }
        else if (newOrdering === 'sum') {
            // noinspection JSUnresolvedVariable
            entries = entries.sort(([,a], [,b]) => a.sum > b.sum ? 1 : -1);
        }
        else if (newOrdering === '-sum') {
            // noinspection JSUnresolvedVariable
            entries = entries.sort(([,a], [,b]) => a.sum < b.sum ? 1 : -1);
        }
        else if (newOrdering[0] !== '-') {
            entries = entries.sort(([,a], [,b]) => {
                let _a = a[newOrdering];
                let _b = b[newOrdering];
                _a = (!_a) ? 0 : _a;
                _b = (!_b) ? 0 : _b;
                return _a > _b ? 1 : -1;
            });
        }
        else {
            const key = newOrdering.substring(1);
            entries = entries.sort(([,a], [,b]) => {
                let _a = a[key];
                let _b = b[key];
                _a = (!_a) ? 0 : _a;
                _b = (!_b) ? 0 : _b;
                return _a < _b ? 1 : -1;
            });
        }
        setReport(Object.fromEntries(entries));
        setOrdering(newOrdering);
    };

    return (
        <ReportSummaryContext.Provider value={{ordering: ordering, handleClickOrder: handleClickOrder}}>
            <Content id="report-summary">
                <ContentHeader breadcrumbs={breadcrumbs}/>
                <ContentBody><Paper>
                    {(!loading)
                        ? <>
                            {(report)
                                ? <>
                                    <Top report={report}
                                         valuesFilter={valuesFilter} setValuesFilter={setValuesFilter}
                                         isFiltered={Boolean(isFiltered(props.router.urlSearchParams))}
                                         onSubmitFilter={handleSubmitFilter}
                                         onClickResetFilter={handleClickResetFilter}
                                    />
                                    {(!isEmptyObject(report))
                                        ? <div className="report">
                                            <TableLeft report={report}/>
                                            <TableRight report={report}
                                                        days={eachDayOfInterval({
                                                            start: dateRange.from, end: dateRange.to
                                                        })}
                                            />
                                        </div>
                                        : null
                                    }
                                </>
                                : <ErrorFetch onClick={() => fetch()}/>
                            }
                        </>
                        : <Spinner/>
                    }
                </Paper></ContentBody>
            </Content>
        </ReportSummaryContext.Provider>
    );
};

let Component = ReportSummary;
Component = withRouter(Component);
Component = withFetch(Component);

export default connect(
    null,
    (dispatch) => ({
        dockSetExpandReports: bindActionCreators(actionDockSetExpandReports, dispatch),
    })
)(Component);
